// local hook used in the context of reports page
import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import { localiseMeasurement } from '@/utils/unitSystem';
import { PoiAccessibilityEnum, ReportInternalType, UnitsEnum } from '@/__generated__/graphql';
import { BLANK_SPACE } from '@/constants';

export const useReportSummary = () => {
  const selectedReport = useAtomValue(state.selectedReport);
  const unitConversionSystem = useAtomValue(state.unitSystem);

  const reportSummaryMemo = useMemo(() => {
    const length = selectedReport?.pointOfInterests?.length;
    const equipmentsList =
      selectedReport?.pointOfInterests?.map((poi) => poi.assembly.tagName) || [];
    const uniqueEquipments = [...new Set(equipmentsList)];
    const totalArea = selectedReport?.pointOfInterests?.reduce((accumulator, object) => {
      const targetKey = object.template?.fields.find(
        (field) => field.type === 'MEASUREMENT_PAINT_AREA'
      )?.key;
      if (typeof targetKey === 'string') {
        const areaValue: string | number | undefined | null =
          object?.templateFieldData?.[targetKey];

        if (areaValue) {
          const stringifiedArea = String(areaValue);
          return (
            accumulator +
            Number.parseFloat(
              stringifiedArea && stringifiedArea !== BLANK_SPACE ? stringifiedArea : '0'
            )
          );
        }
      }
      return accumulator;
    }, 0);
    const totalAreaConverted = localiseMeasurement({
      unitSystem: unitConversionSystem,
      sourceUnit: UnitsEnum.SquareMeter,
      value: totalArea ?? 0,
    });

    const poisInBlocks = [
      ...new Set(
        selectedReport?.pointOfInterests
          ?.filter((poi) => poi.paintRegion?.name)
          ?.map((poi) => poi.paintRegion?.name)
      ),
    ];
    const poisDecks = [
      ...new Set(
        selectedReport?.pointOfInterests?.map((poi) => poi.paintRegion?.deck.name).filter(Boolean)
      ),
    ];

    const inaccessiblePois = selectedReport?.pointOfInterests?.reduce((accumulator, current) => {
      if (current?.accessibility === PoiAccessibilityEnum.ScaffoldingRequired) {
        return accumulator + 1;
      }
      return accumulator;
    }, 0);

    const serviceGroups = [
      ...new Set(
        selectedReport?.pointOfInterests
          ?.filter((poi) => poi.assembly.service?.group)
          ?.map((poi) => poi.assembly.service?.group)
      ),
    ];

    return {
      name: selectedReport?.name ?? '',
      pois: length ?? 0,
      equipments:
        selectedReport?.internalType === ReportInternalType.Component
          ? selectedReport.components?.length ?? 0
          : uniqueEquipments.length,
      completion: 34,
      area: totalAreaConverted,
      type: 'Piping',
      paintBlocks: poisInBlocks,
      inaccessiblePois,
      serviceGroups,
      poiDecks: poisDecks,
    };
  }, [selectedReport, unitConversionSystem]);

  return reportSummaryMemo;
};
